import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { fmtMoney } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function Catalog({ store, categories }) {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [cat, setCat] = useState(params.get("category") || "");
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const acc = store.secondary_color || "#3DBB2C";

  useEffect(() => { api.get(`/public/store/${slug}/products`).then((r) => setProducts(r.data)).catch(() => setProducts([])); }, [slug]);

  const sizes = useMemo(() => [...new Set((products || []).flatMap((p) => (p.variants || []).map((v) => v.size)))].filter(Boolean), [products]);
  const colors = useMemo(() => [...new Set((products || []).flatMap((p) => (p.variants || []).map((v) => v.color)))].filter(Boolean), [products]);

  const filtered = (products || []).filter((p) =>
    (!cat || p.category === cat) &&
    (!size || (p.variants || []).some((v) => v.size === size)) &&
    (!color || (p.variants || []).some((v) => v.color === color))
  );

  const Chip = ({ active, onClick, children, tid }) => (
    <button onClick={onClick} data-testid={tid}
      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "text-white border-transparent" : "text-neutral-700 border-black/10 bg-white hover:border-black/40"}`}
      style={active ? { background: acc } : {}}>{children}</button>
  );

  return (
    <div className="max-w-6xl mx-auto px-5 py-10">
      <h1 className="font-display text-4xl font-bold text-neutral-900 mb-6">Catalogue</h1>
      <div className="space-y-3 mb-8">
        <div className="flex flex-wrap gap-2 items-center"><span className="text-xs text-neutral-500 w-16">Catégorie</span>
          <Chip active={!cat} onClick={() => setCat("")}>Toutes</Chip>
          {categories.map((c) => <Chip key={c} active={cat === c} onClick={() => setCat(c)} tid="filter-category">{c}</Chip>)}
        </div>
        {sizes.length > 0 && <div className="flex flex-wrap gap-2 items-center"><span className="text-xs text-neutral-500 w-16">Taille</span>
          <Chip active={!size} onClick={() => setSize("")}>Toutes</Chip>
          {sizes.map((s) => <Chip key={s} active={size === s} onClick={() => setSize(s)} tid="filter-size">{s}</Chip>)}</div>}
        {colors.length > 0 && <div className="flex flex-wrap gap-2 items-center"><span className="text-xs text-neutral-500 w-16">Couleur</span>
          <Chip active={!color} onClick={() => setColor("")}>Toutes</Chip>
          {colors.map((c) => <Chip key={c} active={color === c} onClick={() => setColor(c)}>{c}</Chip>)}</div>}
      </div>

      {products === null ? <div className="text-neutral-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Chargement…</div> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {filtered.map((p) => {
            const promo = p.sale_price && p.sale_price < p.price;
            return (
              <Link key={p.id} to={`/store/${slug}/produit/${p.id}`} className="group" data-testid="catalog-product-card">
                <div className="aspect-[3/4] bg-neutral-100 rounded-lg overflow-hidden">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                <div className="mt-3 text-sm font-medium text-neutral-900">{p.name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="mono text-sm" style={{ color: acc }}>{fmtMoney(promo ? p.sale_price : p.price)}</span>
                  {promo && <span className="mono text-xs text-neutral-400 line-through">{fmtMoney(p.price)}</span>}
                </div>
              </Link>
            );
          })}
          {filtered.length === 0 && <p className="text-neutral-500 col-span-full text-center py-10">Aucun produit</p>}
        </div>
      )}
    </div>
  );
}
